import {
  Box,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
} from '@mui/material';
import { CaretDownIcon, CaretUpIcon } from '@phosphor-icons/react';
import { getRouteApi } from '@tanstack/react-router';
import type { ChangeEvent } from 'react';
import { Fragment, useEffect, useMemo, useState } from 'react';

import { ClickableTableRow } from '@/components/management/table/ClickableTableRow';
import { useIsAdmin } from '@/hooks/useIsAdmin';
import type { Station } from '@/modules/api/backend';
import { FacilityFilter } from '@/types/FacilityFilter';
import { formatDate } from '@/utils/formatDate';

interface StationTableProps {
  facilities?: Station[];
  filter: FacilityFilter;
  userOrgId?: string;
}

const routeApi = getRouteApi('/_authenticated/management/stations/');

export function StationsTable({ facilities = [], filter, userOrgId }: StationTableProps) {
  const [openRow, setOpenRow] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const navigate = routeApi.useNavigate();

  const isAdmin = useIsAdmin();

  const handleChangePage = (_: unknown, newPage: number) => setPage(newPage);

  const handleChangeRowsPerPage = (event: ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const allFacilities = useMemo(
    () =>
      facilities.flatMap((station) =>
        (station.parkAndRideFacilities ?? []).map((facility) => ({ facility, parentStation: station })),
      ),
    [facilities],
  );

  useEffect(() => {
    setPage(0);
  }, [filter]);

  return (
    <Fragment>
      <TableContainer>
        <Table sx={{ tableLayout: 'fixed', width: '100%' }}>
          <colgroup>
            <Box component="col" sx={{ width: { xs: '10%', lg: '5%' } }} />
            <Box component="col" sx={{ width: { xs: 'auto', lg: '15%' } }} />
            <Box component="col" sx={{ width: { xs: 'auto', lg: '40%' } }} />
            <Box component="col" sx={{ width: { xs: '10%', lg: '10%' } }} />
            <Box component="col" sx={{ width: { xs: '10%', lg: '10%' } }} />
            <Box component="col" sx={{ width: { xs: '10%', lg: '10%' } }} />
            <col style={{ width: '10%' }} />
          </colgroup>

          <TableHead>
            <TableRow>
              <TableCell>Typ</TableCell>
              <TableCell>Nummer</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Sichtbarkeit</TableCell>
              <TableCell>Anlagen</TableCell>
              <TableCell>Zuletzt geändert</TableCell>
              <TableCell>Organisation</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {filter === FacilityFilter.PARKING_FACILITIES
              ? allFacilities
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map(({ facility, parentStation }) => {
                    const clickable = isAdmin || facility.organization?.id === userOrgId;

                    const goFacility = async () =>
                      navigate({
                        to: '/management/stations/$stationId/park-and-ride-facilities/$parkAndRideFacilityId',
                        params: { stationId: parentStation.id, parkAndRideFacilityId: facility.id },
                      });

                    return (
                      <ClickableTableRow
                        key={facility.id}
                        clickable={clickable}
                        onClick={clickable ? goFacility : undefined}
                      >
                        <TableCell>P+R</TableCell>
                        <TableCell>{facility.rmvId}</TableCell>
                        <TableCell>{facility.name}</TableCell>
                        <TableCell>{facility.publication ? 'Öffentlich' : 'Versteckt'}</TableCell>
                        <TableCell>-</TableCell>
                        <TableCell>{formatDate(facility.updatedAt)}</TableCell>
                        <TableCell>{facility.organization?.name ?? '-'}</TableCell>
                      </ClickableTableRow>
                    );
                  })
              : facilities.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((station) => {
                  const goStation = async () =>
                    navigate({
                      to: '/management/stations/$stationId',
                      params: { stationId: station.id },
                    });

                  return (
                    <Fragment key={station.id}>
                      <ClickableTableRow clickable onClick={goStation}>
                        <TableCell>
                          {filter === FacilityFilter.ALL && (station.parkAndRideFacilities?.length ?? 0) > 0 && (
                            <IconButton
                              size="small"
                              onClick={(event) => {
                                // wichtig: Row-Navigation verhindern
                                event.preventDefault();
                                event.stopPropagation();
                                setOpenRow((prev) => (prev === station.id ? null : station.id));
                              }}
                            >
                              {openRow === station.id ? <CaretUpIcon /> : <CaretDownIcon />}
                            </IconButton>
                          )}
                          {filter === FacilityFilter.STATIONS && 'Station'}
                        </TableCell>
                        <TableCell>{station.rmvId}</TableCell>
                        <TableCell>{station.name}</TableCell>
                        <TableCell>-</TableCell>
                        <TableCell>{station.parkAndRideFacilities?.length ?? 0}</TableCell>
                        <TableCell>{formatDate(station.updatedAt)}</TableCell>
                        <TableCell>
                          {station.parkAndRideFacilities
                            ? Array.from(
                                new Set(station.parkAndRideFacilities.map((f) => f.organization?.name).filter(Boolean)),
                              ).join(', ')
                            : ''}
                        </TableCell>
                      </ClickableTableRow>

                      {filter === FacilityFilter.ALL &&
                        openRow === station.id &&
                        (station.parkAndRideFacilities ?? []).map((facility) => {
                          const clickable = isAdmin || facility.organization?.id === userOrgId;

                          const goFacility = async () =>
                            navigate({
                              to: '/management/stations/$stationId/park-and-ride-facilities/$parkAndRideFacilityId',
                              params: { stationId: station.id, parkAndRideFacilityId: facility.id },
                            });

                          return (
                            <ClickableTableRow
                              key={facility.id}
                              clickable={clickable}
                              onClick={clickable ? goFacility : undefined}
                            >
                              <TableCell />
                              <TableCell>{facility.rmvId}</TableCell>
                              <TableCell>{facility.name}</TableCell>
                              <TableCell>{facility.publication ? 'Öffentlich' : 'Versteckt'}</TableCell>
                              <TableCell>-</TableCell>
                              <TableCell>{formatDate(facility.updatedAt)}</TableCell>
                              <TableCell>{facility.organization?.name ?? facility.organizationId}</TableCell>
                            </ClickableTableRow>
                          );
                        })}
                    </Fragment>
                  );
                })}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={filter === FacilityFilter.PARKING_FACILITIES ? allFacilities.length : facilities.length}
        labelRowsPerPage="Zeige"
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        page={page}
        rowsPerPage={rowsPerPage}
        rowsPerPageOptions={[10, 25, 50]}
      />
    </Fragment>
  );
}
